// src/components/Navigation.js
import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import { useAuth } from "../../context/AuthContext";
import Search from "./Search";
import ProfileView from "../Profile/ProfileView";
import NavigationHeader from "./NavigationHeader";
import NavigationMenuDrawer from "./NavigationMenuDrawer";
import { ColorModeContext } from "../../Theme";
import { useTheme } from "@mui/material/styles";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";

const Navigation = ({ onRegister, isAdmin }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const colorMode = useContext(ColorModeContext);
  const theme = useTheme();

  // Drawer states for the menu and search drawers.
  const [menuDrawerOpen, setMenuDrawerOpen] = useState(false);
  const [searchDrawerOpen, setSearchDrawerOpen] = useState(false);

  // State for controlling the Profile popup/modal.
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  // New state for logout confirmation dialog.
  const [confirmLogoutOpen, setConfirmLogoutOpen] = useState(false);

  const toggleMenuDrawer = (open) => () => setMenuDrawerOpen(open);
  const toggleSearchDrawer = (open) => () => setSearchDrawerOpen(open);

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (err) {
      console.error("Logout error:", err);
    }
  };

  // When a user is selected in the search component, open the modal.
  const handleSelectUser = (selectedUser) => {
    if (selectedUser) {
      setSelectedUserId(selectedUser.id);
      setSearchDrawerOpen(false);
      setProfileModalOpen(true);
    }
  };

  // Build menu items for the left drawer.
  const menuItems = [];
  if (user) {
    menuItems.push({
      text: "Low Round",
      action: () => {
        navigate("/");
        setMenuDrawerOpen(false);
      },
    });
    menuItems.push({
      text: "Enter New Round",
      action: () => {
        navigate("/scorecard");
        setMenuDrawerOpen(false);
      },
    });
    menuItems.push({
      text: "Round History",
      action: () => {
        navigate("/scorecard-history");
        setMenuDrawerOpen(false);
      },
    });
    menuItems.push({
      text: "Teams",
      action: () => {
        navigate("/teams");
        setMenuDrawerOpen(false);
      },
    });
    menuItems.push({
      text: "Profile",
      action: () => {
        navigate("/profile");
        setMenuDrawerOpen(false);
      },
    });
    if (isAdmin) {
      menuItems.push({
        text: "Administration",
        action: () => {
          navigate("/admin");
          setMenuDrawerOpen(false);
        },
      });
    }
    menuItems.push({
      text: "Logout",
      action: () => {
        setConfirmLogoutOpen(true);
        setMenuDrawerOpen(false);
      },
    });
  } else {
    menuItems.push({
      text: "Login",
      action: () => {
        navigate("/login");
        setMenuDrawerOpen(false);
      },
    });
    menuItems.push({
      text: "Register",
      action: () => {
        onRegister();
        setMenuDrawerOpen(false);
      },
    });
  }

  // Dark/Light Mode Toggle: Show DarkModeIcon in light mode and LightModeIcon in dark mode.
  menuItems.push({
    text: theme.palette.mode === "light" ? <DarkModeIcon /> : <LightModeIcon />,
    action: () => {
      colorMode.toggleColorMode();
      setMenuDrawerOpen(false);
    },
  });

  return (
    <>
      <NavigationHeader
        onMenuClick={toggleMenuDrawer(true)}
        onTitleClick={() => navigate("/")}
        onSearchClick={toggleSearchDrawer(true)}
        user={user}
      />
      <NavigationMenuDrawer
        open={menuDrawerOpen}
        onClose={toggleMenuDrawer(false)}
        menuItems={menuItems}
      />
      {user && (
        <Search
          open={searchDrawerOpen}
          onClose={toggleSearchDrawer(false)}
          onSelectUser={handleSelectUser}
        />
      )}
      {user && selectedUserId && (
        <ProfileView
          open={profileModalOpen}
          onClose={() => setProfileModalOpen(false)}
          userId={selectedUserId}
        />
      )}
      <Dialog
        open={confirmLogoutOpen}
        onClose={() => setConfirmLogoutOpen(false)}
      >
        <DialogTitle>Confirm Logout</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to log out?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => setConfirmLogoutOpen(false)}
            sx={{
              backgroundColor: "primary.main",
              color: "black",
              "&:hover": { backgroundColor: "primary.dark" },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setConfirmLogoutOpen(false);
              handleLogout();
            }}
            sx={{
              backgroundColor: "primary.main",
              color: "black",
              "&:hover": { backgroundColor: "primary.dark" },
            }}
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Navigation;